import { AGENT_APP_COMMAND_STATUSES, agentAppCommandSchema } from '@poweralpha/common';
import { z } from 'zod';

export const CONTRACT_ID = 'agent-state-1' as const;

const id = z.string().min(1);

const counter = z.string().regex(/^(0|[1-9][0-9]*)$/);

const timestamp = z.iso.datetime({ offset: true });

const status = z.enum(['queued', 'running', 'complete', 'cancelled', 'error', 'interrupted']);

const envelope = { contract_id: z.literal(CONTRACT_ID) };

export const cursorSchema = z.object({ conversation_id: id, sequence: counter });

export const attachmentSchema = z.object({
  upload_id: id,
  filename: z.string(),
  media_type: z.string(),
  kind: z.enum(['data', 'text', 'image']),
  byte_size: z.number().int().nonnegative(),
});

const reference = z.object({
  resource_type: z.string(),
  resource_id: id,
  version: z.number().int().nullable(),
  label: z.string(),
});

const point = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  label: z.string(),
  feature_kind: z.enum(['location', 'project', 'weather', 'met_station']),
  resource_type: z.enum(['project', 'weather', 'met_station']).nullable(),
  resource_id: id.nullable(),
});

const jsonObject = z.record(z.string(), z.json());

const blockSchema = z.discriminatedUnion('kind', [
  z.object({ id, kind: z.literal('markdown'), text: z.string() }),
  z.object({
    id,
    kind: z.literal('plot'),
    data: z.array(jsonObject),
    layout: jsonObject,
    config: jsonObject.nullable(),
  }),
  z.object({
    id,
    kind: z.literal('map'),
    map_id: id,
    title: z.string().nullable(),
    points: z.array(point),
  }),
  z.object({
    id,
    kind: z.literal('artifact'),
    artifact_id: id,
    artifact_kind: z.string().nullable(),
    filename: z.string().nullable(),
    byte_size: z.number().nullable(),
    download_url: z.string().nullable(),
  }),
  z.object({ id, kind: z.literal('references'), references: z.array(reference) }),
  // A command for the live app; applied once, only in the tab that sent the turn.
  z.object({
    id,
    kind: z.literal('app_command'),
    command: agentAppCommandSchema,
    origin_client_id: z.string().nullable(),
    // What the origin tab reported (`app_command_ack`); defaults keep earlier transcripts valid.
    status: z.enum(AGENT_APP_COMMAND_STATUSES).default('sent'),
    reason: z.string().nullable().default(null),
    status_at: timestamp.nullable().default(null),
  }),
]);

export const entrySchema = z.discriminatedUnion('kind', [
  z.object({
    id,
    kind: z.literal('user'),
    created_at: timestamp,
    text: z.string(),
    attachments: z.array(attachmentSchema),
    steer: z
      .object({
        steer_id: id,
        client_steer_id: id.nullable(),
        status: z.enum(['accepted', 'applied', 'expired']),
        reason: z.string().nullable(),
      })
      .nullable(),
  }),
  z.object({
    id,
    kind: z.literal('assistant'),
    created_at: timestamp,
    blocks: z.array(blockSchema),
  }),
  z.object({
    id,
    kind: z.literal('activity'),
    created_at: timestamp,
    items: z.array(
      z.object({
        id,
        kind: z.enum([
          'thinking',
          'tool',
          'skill',
          'knowledge',
          'sandbox',
          'context',
          'usage',
          'warning',
        ]),
        label: z.string(),
        detail: z.string().nullable(),
        tool_name: z.string().nullable(),
        status: z.enum(['active', 'ok', 'retry', 'error']),
      }),
    ),
  }),
]);

const failure = z.object({ code: z.string(), message: z.string() });

const turnState = z.object({
  status,
  started_at: timestamp.nullable(),
  finished_at: timestamp.nullable(),
  output_started_at: timestamp.nullable(),
  first_output_at: timestamp.nullable(),
  cancel_requested_at: timestamp.nullable(),
  error: failure.nullable(),
});

export const turnSchema = z.object({
  turn_id: id,
  client_turn_id: id.nullable(),
  turn_index: z.number().int().nonnegative(),
  revision: counter,
  created_at: timestamp,
  state: turnState,
  entries: z.array(entrySchema),
});

const conversationData = z.object({
  conversation_id: id,
  revision: counter,
  title: z.string(),
  created_at: timestamp,
  updated_at: timestamp,
  archived_at: timestamp.nullable(),
  turn_count: z.number().int(),
  latest_turn_id: id.nullable(),
  latest_turn_status: status.nullable(),
  active_turn_id: id.nullable(),
});

export const conversationSchema = conversationData.extend({
  shared_by: z.string().nullable(),
  capabilities: z.object({
    submit: z.boolean(),
    cancel: z.boolean(),
    steer: z.boolean(),
    share: z.boolean(),
    feedback: z.boolean(),
    archive: z.boolean(),
  }),
});

export const feedbackSchema = z.object({
  revision: counter,
  rating: z.union([z.literal(-1), z.literal(0), z.literal(1)]),
  reasons: z.array(z.string()),
  comment: z.string().nullable(),
  updated_at: timestamp.nullable(),
});

export const snapshotSchema = z.object({
  ...envelope,
  conversation: conversationSchema,
  resume_cursor: cursorSchema,
  turns: z.array(turnSchema),
  feedback_by_turn_id: z.record(z.string(), feedbackSchema),
  has_older_turns: z.boolean(),
  next_before_turn_index: z.number().int().nullable(),
});

export const listSchema = z.object({
  ...envelope,
  items: z.array(
    conversationSchema.extend({
      last_prompt: z.string().nullable(),
      // Widgets in the caller's own workspace for the conversation (poweralpha #2359).
      // Optional until that API is deployed everywhere.
      workspace_widget_count: z.number().int().nonnegative().optional(),
    }),
  ),
  next_offset: z.number().int().nullable(),
});

export const activeSchema = z.object({
  ...envelope,
  items: z.array(
    z.object({ conversation: conversationSchema, turn: turnSchema, feedback: feedbackSchema }),
  ),
});

export const singleTurnSchema = z.object({
  ...envelope,
  conversation_id: id,
  turn: turnSchema,
  feedback: feedbackSchema,
});

export const feedbackResponseSchema = z.object({
  ...envelope,
  conversation_id: id,
  turn_id: id,
  feedback: feedbackSchema,
});

export const archiveSchema = z.object({ ...envelope, conversation: conversationSchema });

const workspaceWidgetKind = z.enum(['plot', 'map', 'mapView', 'code', 'markdown', 'table']);
const count = z.number().int().nonnegative();

/** A conversation workspace: transcript references plus grid layout, never content. */
export const workspaceSchema = z.object({
  ...envelope,
  conversation_id: id,
  title: z.string().nullable(),
  widgets: z.array(
    z.object({
      id: z.string().min(1),
      kind: workspaceWidgetKind,
      source: z.object({ turn_id: id, block_id: id, part_index: count.nullish() }),
    }),
  ),
  layout: z.array(z.object({ i: z.string().min(1), x: count, y: count, w: count, h: count })),
  revision: count,
  updated_at: timestamp.nullable(),
});
export type ServerWorkspace = z.infer<typeof workspaceSchema>;

export const workspaceConflictSchema = z.object({
  ...envelope,
  error: failure,
  workspace: workspaceSchema,
});

const patchSchema = z.object({
  base_revision: counter,
  revision: counter,
  state: turnState,
  entry_order: z.array(id),
  put_entries: z.array(entrySchema),
  append_text: z.array(
    z.discriminatedUnion('kind', [
      z.object({ kind: z.literal('markdown'), entry_id: id, block_id: id, text: z.string() }),
      z.object({ kind: z.literal('thinking'), entry_id: id, activity_id: id, text: z.string() }),
    ]),
  ),
});

const changeBase = { sequence: counter, recorded_at: timestamp };

export const changeSchema = z.discriminatedUnion('type', [
  z.object({ ...changeBase, type: z.literal('turn_created'), turn: turnSchema }),
  z.object({ ...changeBase, type: z.literal('turn_updated'), turn_id: id, patch: patchSchema }),
  z.object({
    ...changeBase,
    type: z.literal('conversation_updated'),
    conversation: conversationData,
  }),
]);

const subscription = { ...envelope, conversation_id: id, subscription_id: id };

const command = {
  ...envelope,
  type: z.literal('command_result'),
  request_id: id,
  command_id: id,
  conversation_id: id,
};

const ackResult = {
  ...envelope,
  type: z.literal('app_command_ack_result'),
  request_id: id,
  conversation_id: id,
  app_command_id: id,
};

export const serverMessageSchema = z.union([
  // `duplicate`: the outcome was already recorded, nothing changed.
  z.object({
    ...ackResult,
    outcome: z.enum(['recorded', 'duplicate']),
    status: z.enum(AGENT_APP_COMMAND_STATUSES),
  }),
  z.object({
    ...ackResult,
    outcome: z.literal('rejected'),
    code: z.enum(['not_found', 'client_mismatch', 'invalid_transition', 'temporarily_unavailable']),
    message: z.string(),
    retryable: z.boolean(),
  }),
  z.object({
    ...subscription,
    type: z.literal('changes'),
    from: cursorSchema,
    through: cursorSchema,
    changes: z.array(changeSchema),
  }),
  z.object({ ...subscription, type: z.literal('subscribed'), request_id: id, after: cursorSchema }),
  z.object({ ...subscription, type: z.literal('caught_up'), through: cursorSchema }),
  z.object({ ...subscription, type: z.literal('access_revoked') }),
  z.object({ ...envelope, type: z.literal('unsubscribed'), request_id: id, conversation_id: id }),
  z.object({
    ...envelope,
    type: z.literal('resync_required'),
    conversation_id: id,
    request_id: id.nullable(),
    subscription_id: id.nullable(),
    reason: z.string(),
    snapshot_path: z.string(),
  }),
  z.object({
    ...command,
    outcome: z.literal('accepted'),
    duplicate: z.boolean(),
    disposition: z.enum(['created', 'requested', 'already_terminal']),
    turn_id: id,
    turn_status: status,
    steer_id: id.nullable(),
    turn_path: z.string(),
  }),
  z.object({
    ...command,
    outcome: z.literal('rejected'),
    code: z.string(),
    message: z.string(),
    retryable: z.boolean(),
    active_turn_id: id.nullable(),
  }),
  z.object({
    ...envelope,
    type: z.literal('request_rejected'),
    request_id: id.nullable(),
    conversation_id: id.nullable(),
    error: failure,
  }),
  z.object({
    ...envelope,
    type: z.literal('session_error'),
    code: z.string(),
    message: z.string(),
    action: z.enum(['reconnect', 'reload']),
  }),
]);

export type Turn = z.infer<typeof turnSchema>;

export type Entry = z.infer<typeof entrySchema>;

export type Conversation = z.infer<typeof conversationSchema>;

export type Snapshot = z.infer<typeof snapshotSchema>;

export type Feedback = z.infer<typeof feedbackSchema>;

export type ServerMessage = z.infer<typeof serverMessageSchema>;

export type ChangeBatch = Extract<ServerMessage, { type: 'changes' }>;

export type CommandResult = Extract<ServerMessage, { type: 'command_result' }>;

export type AppCommandAckResult = Extract<ServerMessage, { type: 'app_command_ack_result' }>;

export const isActive = (turn: Turn | undefined) =>
  turn?.state.status === 'queued' || turn?.state.status === 'running';

export const newer = (revision: string, previous: string) => BigInt(revision) > BigInt(previous);
